import { useState } from 'react';
import { BACKEND_URL } from '../App';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ImageOff, X } from 'lucide-react';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

/**
 * Photo gallery for prize listings.
 * - Hero image + thumbnail strip
 * - Click to open full-screen lightbox
 */
export function PhotoGallery({ photos = [], alt = 'Prize photo' }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!photos || photos.length === 0) {
    return (
      <div data-testid="photo-gallery-empty" className="aspect-video bg-slate-800/40 border border-slate-700/40 rounded-xl flex flex-col items-center justify-center text-slate-600">
        <ImageOff className="w-7 h-7 mb-1" />
        <p className="text-[10px]">No photos provided</p>
      </div>
    );
  }

  const heroUrl = resolveUrl(photos[activeIdx]);

  return (
    <>
      <div data-testid="photo-gallery" className="space-y-2">
        <button
          type="button"
          data-testid="photo-hero"
          onClick={() => setLightbox(true)}
          className="block w-full aspect-video bg-slate-900 border border-slate-700/40 rounded-xl overflow-hidden active:scale-[0.99] transition-transform"
        >
          <img
            src={heroUrl}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = ''; e.currentTarget.style.display = 'none'; }}
          />
        </button>
        {photos.length > 1 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {photos.map((p, idx) => (
              <button
                type="button"
                key={`${p}-${idx}`}
                data-testid={`photo-thumb-${idx}`}
                onClick={() => setActiveIdx(idx)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIdx ? 'border-orange-500' : 'border-slate-700/40 opacity-70 hover:opacity-100'
                }`}
              >
                <img src={resolveUrl(p)} alt={`thumb ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent
          data-testid="photo-lightbox"
          className="bg-black/95 border-slate-800 max-w-3xl mx-4 p-2 sm:p-4"
        >
          <VisuallyHidden.Root>
            <DialogTitle>{alt}</DialogTitle>
            <DialogDescription>Photo gallery viewer for {alt}</DialogDescription>
          </VisuallyHidden.Root>
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute right-3 top-3 z-50 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-white"
          >
            <X className="w-4 h-4" />
          </button>
          <img
            src={heroUrl}
            alt={alt}
            className="w-full max-h-[80vh] object-contain rounded-lg"
          />
          {photos.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pt-2">
              {photos.map((p, idx) => (
                <button
                  type="button"
                  key={`lb-${p}-${idx}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${
                    idx === activeIdx ? 'border-orange-500' : 'border-slate-700/40 opacity-70'
                  }`}
                >
                  <img src={resolveUrl(p)} alt={`lb thumb ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PhotoGallery;
